import { useEffect, useRef, useState } from 'react';

/**
 * Custom SEO hook for React 19+ that manages document head elements for SEO
 * with title animation for long titles
 * 
 * @param {Object} seoData - Object containing SEO data
 * @param {string} seoData.title - Page title
 * @param {string} seoData.description - Meta description
 * @param {string} seoData.canonical - Canonical URL
 * @param {string} seoData.keywords - Meta keywords
 * @param {string} seoData.ogTitle - Open Graph title
 * @param {string} seoData.ogDescription - Open Graph description
 * @param {string} seoData.ogImage - Open Graph image URL
 * @param {string} seoData.ogUrl - Open Graph URL
 * @param {string} seoData.ogType - Open Graph type (default: "website")
 * @param {string} seoData.twitterCard - Twitter card type (default: "summary_large_image")
 * @param {string} seoData.twitterSite - Twitter site username
 * @param {string} seoData.twitterCreator - Twitter creator username
 * @param {string} seoData.robots - Robots meta tag content
 * @param {Object} seoData.jsonLd - Structured data as JSON-LD object
 * @param {Object} seoData.customMeta - Additional custom meta tags as key-value pairs
 * @param {Object} seoData.titleAnimation - Configuration for title animation
 * @param {boolean} seoData.titleAnimation.enabled - Enable title animation (default: true)
 * @param {number} seoData.titleAnimation.maxLength - Maximum title length before animation (default: 30)
 * @param {number} seoData.titleAnimation.speed - Animation speed in milliseconds (default: 3000)
 * @param {string} seoData.titleAnimation.separator - Separator between title beginning and end (default: " - ")
 */
export default function useSEO({
  title,
  description,
  canonical,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = "website",
  twitterCard = "summary_large_image",
  twitterSite,
  twitterCreator,
  robots,
  jsonLd,
  customMeta = {},
  titleAnimation = {
    enabled: true,
    maxLength: 30,
    speed: 3000,
    separator: " - "
  }
}) {
  // Store created elements for cleanup
  const createdElements = useRef([]);
  
  // Previous title for restoration on unmount
  const prevTitle = useRef(document.title);
  
  // Reference for animation interval
  const animationInterval = useRef(null);
  
  // Original full title
  const fullTitle = useRef(title);

  useEffect(() => {
    // Save previous title on first render
    prevTitle.current = document.title;
    
    // Save the full title
    fullTitle.current = title;

    // Helper function to set meta tags
    const setMetaTag = (name, content, property = false) => {
      if (!content) return null;
      
      const attributeName = property ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attributeName}="${name}"]`);
      
      if (tag) {
        // Update existing tag
        tag.setAttribute('content', content);
        return null;
      } else {
        // Create new tag
        tag = document.createElement('meta');
        tag.setAttribute(attributeName, name);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
        createdElements.current.push(tag);
        return tag;
      }
    };

    // Helper function to set link tags
    const setLinkTag = (rel, href, attributes = {}) => {
      if (!href) return null;
      
      let link = document.querySelector(`link[rel="${rel}"][href="${href}"]`);
      
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute('href', href);
        
        // Set additional attributes
        Object.entries(attributes).forEach(([key, value]) => {
          link.setAttribute(key, value);
        });
        
        document.head.appendChild(link);
        createdElements.current.push(link);
      }
      
      return link;
    };

    // Set title with potential animation
    if (title) {
      // First set the initial title
      document.title = title;
      
      // Clear any existing interval
      if (animationInterval.current) {
        clearInterval(animationInterval.current);
        animationInterval.current = null;
      }
      
      // Check if title animation should be applied
      const shouldAnimate = 
        titleAnimation.enabled && 
        title.length > titleAnimation.maxLength;
      
      if (shouldAnimate) {
        // Create a continuous string with space padding to ensure smooth transition
        // Adding extra spaces for a smoother transition between end and beginning
        const paddedTitle = `${title}${titleAnimation.separator}${title}`;
        let position = 0;
        const stepSize = 1; // Move by 1 character for smoother animation
        const updateFrequency = 200; // Update every 200ms for smooth animation
        
        animationInterval.current = setInterval(() => {
          // Extract the current window of text to display
          let displayText = "";
          
          // If we're near the end of our string, loop back to the beginning for continuity
          if (position + titleAnimation.maxLength > paddedTitle.length) {
            // We need to restart from the beginning smoothly
            // Reset position to beginning of second copy for seamless loop
            position = 0;
          }
          
          displayText = paddedTitle.substring(position, position + titleAnimation.maxLength);
          document.title = displayText;
          
          // Increment position
          position += stepSize;
        }, updateFrequency);
      }
    }

    // Set basic meta tags
    if (description) setMetaTag('description', description);
    if (keywords) setMetaTag('keywords', keywords);
    if (robots) setMetaTag('robots', robots);

    // Set canonical link
    if (canonical) setLinkTag('canonical', canonical);

    // Set Open Graph meta tags
    if (ogTitle) setMetaTag('og:title', ogTitle, true);
    if (ogDescription) setMetaTag('og:description', ogDescription, true);
    if (ogImage) setMetaTag('og:image', ogImage, true);
    if (ogUrl) setMetaTag('og:url', ogUrl, true);
    if (ogType) setMetaTag('og:type', ogType, true);

    // Set Twitter Card meta tags
    if (twitterCard) setMetaTag('twitter:card', twitterCard);
    if (twitterSite) setMetaTag('twitter:site', twitterSite);
    if (twitterCreator) setMetaTag('twitter:creator', twitterCreator);

    // Set custom meta tags
    Object.entries(customMeta).forEach(([name, content]) => {
      setMetaTag(name, content);
    });

    // Set JSON-LD structured data
    if (jsonLd) {
      let script = document.querySelector('script[type="application/ld+json"]');
      
      if (script) {
        script.textContent = JSON.stringify(jsonLd);
      } else {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(script);
        createdElements.current.push(script);
      }
    }

    // Cleanup function that runs when component unmounts
    return () => {
      // Restore previous title
      document.title = prevTitle.current;
      
      // Clear any animation interval
      if (animationInterval.current) {
        clearInterval(animationInterval.current);
        animationInterval.current = null;
      }
      
      // Remove all elements we created
      createdElements.current.forEach(element => {
        if (element && element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
      
      // Clear the elements array
      createdElements.current = [];
    };
  }, [
    title, description, canonical, keywords, 
    ogTitle, ogDescription, ogImage, ogUrl, ogType,
    twitterCard, twitterSite, twitterCreator,
    robots, jsonLd, customMeta, titleAnimation
  ]);
  
  // Return the full title in case it's needed
  return {
    fullTitle: fullTitle.current
  };
}